import { translateSafe } from '@/utils/translations';
import {
  ListFilter,
  Activity,
  MessagesSquare,
  Tag,
  RectangleEllipsis,
  Folders,
  Users2,
  Star,
  Save,
  Clock,
} from 'lucide-react';
import { useMemo } from 'react';
import { ToggleGroup, ToggleGroupItem } from '../primitives/toggle-group';
import {
  FILTERS,
  isConversation,
  isConversationCustomColumn,
  isCustomAggregate,
  isCustomColumn,
  isTag,
} from '@/models/common/filters';
import { useProjectsContext } from '@/providers/projects-provider';

export type FilterCategory =
  | 'all'
  | 'interactions'
  | 'conversations'
  | 'users'
  | 'tags'
  | 'custom_columns'
  | 'conversation_custom_columns'
  | 'custom_aggregates'
  | 'saved_filters'
  | 'user_groups'
  | 'favorites'
  | 'recently_used'
  | 'time';

const ALL_TABS: FilterCategory[] = [
  'all',
  'interactions',
  'conversations',
  'users',
  'user_groups',
  'tags',
  'custom_columns',
  'conversation_custom_columns',
  'custom_aggregates',
  'saved_filters',
  'favorites',
  'recently_used',
];

export default function FilterSidebar({
  filterKind,
  setFilterKind,
  availableFilterKinds,
  activeTab,
  setActiveTab,
  hideSavedFilters = false,
  hideFavoritesAndRecentlyUsedTabs = false,
  extraTabs,
}: {
  filterKind: string | null;
  setFilterKind: (filterKind: string | null) => void;
  availableFilterKinds: string[];
  activeTab: FilterCategory;
  setActiveTab: (tab: FilterCategory) => void;
  hideSavedFilters?: boolean;
  hideFavoritesAndRecentlyUsedTabs?: boolean;
  extraTabs?: FilterCategory[];
}) {
  const { savedFilters } = useProjectsContext();
  const filterCategoryIcons: { [key in FilterCategory]: React.ElementType } = {
    all: ListFilter,
    time: Clock,
    interactions: Activity,
    users: Users2,
    conversations: MessagesSquare,
    user_groups: Users2,
    tags: Tag,
    custom_columns: RectangleEllipsis,
    custom_aggregates: Folders,
    conversation_custom_columns: RectangleEllipsis,
    saved_filters: Save,
    favorites: Star,
    recently_used: Clock,
  };

  const updateActiveTab = (val: string) => {
    if (ALL_TABS.includes(val as FilterCategory) || extraTabs?.includes(val as FilterCategory)) {
      if (filterKind) setFilterKind(null);
      setActiveTab(val as FilterCategory);
    }
  };

  const availableTabs = useMemo(() => {
    const baseTabs = ALL_TABS.filter((item) => {
      if (item === 'saved_filters') {
        return !hideSavedFilters && savedFilters.length > 0;
      }

      if (item === 'favorites' || item === 'recently_used' || item === 'all') {
        if (hideSavedFilters) return false;
        if (hideFavoritesAndRecentlyUsedTabs) return item === 'all';
        return true;
      }

      const filtered = filterKindsByCategory(availableFilterKinds, item);
      return filtered.length > 0;
    });

    if (!extraTabs?.length) return baseTabs;
    // Insert extra tabs right after 'all'
    const allIndex = baseTabs.indexOf('all');
    const insertAt = allIndex >= 0 ? allIndex + 1 : 0;
    return [...baseTabs.slice(0, insertAt), ...extraTabs, ...baseTabs.slice(insertAt)];
  }, [availableFilterKinds, savedFilters, hideSavedFilters, hideFavoritesAndRecentlyUsedTabs, extraTabs]);

  return (
    <div className="w-45 shrink-0 self-stretch overflow-y-auto border-r p-2">
      <ToggleGroup type="single" value={activeTab} onValueChange={updateActiveTab} className="flex flex-col">
        {availableTabs.map((tab) => {
          const Icon = filterCategoryIcons[tab];
          return (
            <ToggleGroupItem key={tab} value={tab} className={`block w-full text-start text-[12px]`}>
              <div className="flex items-center gap-2">
                <Icon size={12} />
                {translateSafe('filter_tabs', tab)}
              </div>
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
}

export const filterKindsByCategory = (filters: string[], category: FilterCategory) => {
  switch (category) {
    case 'all':
      return filters;
    case 'users':
      return filters.filter(
        (f) =>
          f === 'first_interaction' ||
          f === 'last_interaction' ||
          f === 'last_interaction_tag' ||
          f === 'users_interactions' ||
          f === 'user_conversations_count' ||
          f === 'user_conversation_length' ||
          f === 'active_periods' ||
          f === 'avg_session_length',
      );
    case 'user_groups':
      return filters.filter((f) => f === 'cohort');
    case 'interactions':
      return filters.filter(
        (f) =>
          (!isConversation(f) &&
            !isTag(f) &&
            !isCustomColumn(f) &&
            !isCustomAggregate(f) &&
            f !== FILTERS.kindConversationLabel &&
            f !== 'cohort' &&
            f !== 'first_interaction' &&
            f !== 'last_interaction' &&
            f !== 'last_interaction_tag' &&
            f !== 'users_interactions' &&
            f !== 'user_conversations_count' &&
            f !== 'user_conversation_length' &&
            f !== 'active_periods' &&
            f !== 'avg_session_length') ||
          f === FILTERS.kindInteractionLabel,
      );
    case 'conversations':
      return filters.filter((f) => isConversation(f) || f === FILTERS.kindConversationLabel);
    case 'tags':
      return filters.filter((f) => isTag(f));
    case 'custom_columns':
      return filters.filter((f) => isCustomColumn(f));
    case 'conversation_custom_columns':
      return filters.filter((f) => isConversationCustomColumn(f));
    case 'custom_aggregates':
      return filters.filter((f) => isCustomAggregate(f));
    default:
      return filters;
  }
};
